/**
 * Theme styling for the Giftnote app's shadow DOM — the cart button and the modal.
 *
 * The app renders its button inside an OPEN shadow root and ships its own stylesheet in there:
 *
 *   #container { width:100%; display:flex; justify-content:center; }
 *   #button    { width:100%; height:50px; border-radius:6px; ... }
 *   @media screen and (max-width:740px) { #button { height:50px; width:100%; } }
 *
 * It exposes no ::part() hooks and no CSS custom properties, so ordinary theme CSS — including
 * `!important` and selectors like `.cart__giftnote button` — cannot cross the shadow boundary
 * and has no effect. The only ways in are the app embed's `custom_styles` setting, or adopting a
 * stylesheet into the open shadow root, which is what this module does.
 *
 * SCOPE: the cart footer button always, and the product page button only in gift mode (see below).
 * Outside gift mode the product page button is a separate app block the merchant styles in the
 * theme editor (centered, 30px radius, 16px type) and is deliberately left alone — the app embed's
 * `custom_styles` setting could not be used for either, since it applies to every placement at
 * once. Scoping is by `.cart__giftnote`, the class this theme puts on the element in
 * snippets/cart-summary.liquid, rather than the app's `data-gn-placement` attribute, because our
 * class is in the markup from first paint while the attribute only appears on hydration.
 *
 * GIFT MODE: a shopper arriving from /pages/gifting carries `?gift=true`, and
 * snippets/yc-gift-mode.liquid turns that into `data-gift-mode="true"` on <html> before first
 * paint. That snippet demotes Add to Cart to the secondary button with ordinary CSS; this module
 * does the other half, because neither the gift button's colours (behind a shadow boundary) nor
 * its position (an app block wrapper between theme blocks) can be reached from a stylesheet.
 *
 * The styles below make it read as a secondary action beneath the checkout button: same full width,
 * same 50px height and pill radius, in a tint of the checkout button's own colour. The tokens
 * resolve because custom properties inherit across a shadow boundary even though selectors do not
 * (verified live: --color-primary-button-background reads rgb(64, 54, 214) inside the shadow root).
 *
 * The media-query block is NOT redundant: the app re-declares #button under max-width:740px, so
 * without repeating the rules there the button reverts to the app's own look on mobile.
 */

/** The cart footer button only — never the product page app block. */
const BUTTON_SELECTOR = 'giftnote-button.cart__giftnote';

const BUTTON_RULES = `
    width: 100%;
    height: 50px;
    padding: 0 var(--padding-4xl, 2rem);
    border: none;
    border-radius: var(--style-border-radius-buttons-primary, 100px);
    background-color: rgb(from var(--color-primary-button-background, #4036d6) r g b / 0.12);
    color: var(--color-primary-button-background, #4036d6);
    font-family: var(--font-paragraph--family, inherit);
    font-size: 14px;
    font-weight: 700;
    letter-spacing: normal;
`;

const BUTTON_STYLES = `
  #container { justify-content: stretch; }

  #button { ${BUTTON_RULES} }

  @media screen and (max-width: 740px) {
    #container { justify-content: stretch; }

    #button { ${BUTTON_RULES} }
  }
`;

const giftMode = document.documentElement.getAttribute('data-gift-mode') === 'true';

/*
  The product page app block only. Scoped by `.product-details` rather than by excluding the cart
  footer's `.cart__giftnote`, because the cart drawer is global: on a product page with items in
  the cart, the app injects its own per-line-item buttons (`.cart-items__giftnote`) into that
  drawer, and those must keep their own look.
*/
const PRODUCT_BUTTON_SELECTOR = '.product-details giftnote-button';

/*
  Gift mode only: promote the product page button to a filled primary CTA. Size, radius, alignment
  and type stay on the merchant's app block settings — just the fill changes, which is the whole
  point of the swap.

  WHY INK AND NOT `--color-primary-button-background`: the theme's primary button is the indigo
  `color_palette.color3`, which is also the fill of the "MOST POPULAR" bar on the Subscribe & Save
  card directly below this button. Two indigo blocks stacked read as one element and the CTA stops
  announcing itself. `--color-foreground` is the same ink the page's body copy uses — the token
  `settings.color_palette.foreground` that the add-to-cart block's own custom-colour defaults point
  at — so the button is black against white and distinct from everything around it.

  `!important` is required here and nowhere else in this file: the app writes this block's
  merchant-configured colours as inline styles on #button, which an adopted stylesheet cannot
  outrank. This is the third-party-app exception in ruleset.md, not a specificity shortcut.
*/
const PRODUCT_BUTTON_GIFT_RULES = `
    background-color: var(--color-foreground, #000000) !important;
    color: var(--color-background, #ffffff) !important;
    border: none !important;
`;

/*
  There is no hover token pair for an ink-filled button — the theme only ships them for the primary
  and secondary palettes — so the hover is derived from the same ink rather than hardcoded, which
  keeps it correct if the palette changes.
*/
const PRODUCT_BUTTON_GIFT_HOVER_RULES = `
    background-color: rgb(from var(--color-foreground, #000000) r g b / 0.85) !important;
    color: var(--color-background, #ffffff) !important;
`;

const PRODUCT_BUTTON_GIFT_STYLES = `
  #button { ${PRODUCT_BUTTON_GIFT_RULES} }

  #button:hover { ${PRODUCT_BUTTON_GIFT_HOVER_RULES} }

  @media screen and (max-width: 740px) {
    #button { ${PRODUCT_BUTTON_GIFT_RULES} }
  }
`;

/** The modal lives in <giftnote-core>'s own shadow root, separate from the buttons'. */
const CORE_SELECTOR = 'giftnote-core';

const MODAL_STYLES = `
  /*
    Every field is a bordered wrapper containing a control that the app also rings with a 1px
    box-shadow, so each field reads as a box inside a box. Drop the inner ring and keep the
    wrapper's. These are shadows, not borders — a border reset would do nothing.
  */
  #giftnote-modal :is(input, textarea, select) {
    box-shadow: none;
  }

  /*
    The app's focus state only swaps that ring's colour and sets its spread to 0, so focused
    controls have no visible indicator at all. Supply one from the theme (WCAG 2.4.7).
  */
  #giftnote-modal :is(input, textarea, select):focus-visible {
    outline: var(--focus-outline-width, 2px) solid var(--color-foreground, #2e2e2e);
    outline-offset: 2px;
  }
`;

const supportsConstructedSheets =
  typeof CSSStyleSheet === 'function' &&
  (() => {
    try {
      new CSSStyleSheet();
      return true;
    } catch {
      return false;
    }
  })();

/** One constructed sheet per rule set, shared by every shadow root that needs it. */
const sheets = new Map();

/** @param {string} css */
function getSharedSheet(css) {
  let sheet = sheets.get(css);
  if (!sheet) {
    sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    sheets.set(css, sheet);
  }
  return sheet;
}

/**
 * @param {Element} element - a host with an open shadow root
 * @param {string} css
 * @param {string} marker - dedupes the fallback <style> when sheets are unsupported
 */
function applyStyles(element, css, marker) {
  const root = element.shadowRoot;
  // The app attaches the shadow root asynchronously; a later pass picks this element up.
  if (!root) return;

  if (supportsConstructedSheets) {
    const sheet = getSharedSheet(css);
    if (root.adoptedStyleSheets.includes(sheet)) return;
    root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
    return;
  }

  // Fallback for browsers without constructable stylesheets.
  if (root.querySelector(`[${marker}]`)) return;
  const style = document.createElement('style');
  style.setAttribute(marker, '');
  style.textContent = css;
  root.appendChild(style);
}

/**
 * Gift mode only: move the gift button's app block into the buy row, directly above Add to Cart.
 *
 * The app block ships as a sibling of the whole `buy-buttons` block, but that block is not just
 * the button — with `yc_po_skin` on it also renders "Step 2 — Pick your order type" and the
 * Subscribe & Save cards. So moving the gift block to before its sibling lands it between Step 1
 * and Step 2, which reads as 1 → gift CTA → 2. The only position that is actually above Add to
 * Cart is inside `.product-form-buttons`, which is why this reparents rather than reorders.
 *
 * Flexbox `order` cannot stand in for the move: the gift block is not a child of that container to
 * begin with, and in its own container `order` is scoped to all six siblings rather than to a pair.
 *
 * The anchor is the quantity stepper when there is one, not Add to Cart itself: the two share a
 * line (`.pdp__buy-row`), so inserting between them would strand the stepper on a row of its own.
 *
 * SAFE INSIDE THE FORM: the app renders its `#button` inside an open shadow root, and form
 * association does not cross a shadow boundary, so it cannot submit the add-to-cart form.
 *
 * SELF-HEALING: a variant change can morph `<main>` or `.product-form-buttons` back to the server's
 * markup, which has the block in its original place. The MutationObserver below re-runs this, and
 * every step is idempotent.
 */
function promoteProductGiftButton() {
  const giftButton = document.querySelector(PRODUCT_BUTTON_SELECTOR);
  const addToCart = document.querySelector('.product-details .add-to-cart-button');
  if (!giftButton || !addToCart) return;

  const row = addToCart.closest('.product-form-buttons');
  if (!row) return;

  // Walk up from each button to the block wrapper that is a direct child of its own container.
  const addToCartBlock = blockWrapper(addToCart, row);
  const giftBlock = blockWrapper(giftButton, null);
  if (!addToCartBlock || !giftBlock) return;

  // The stepper shares Add to Cart's line, so the gift button goes above both.
  const previous = addToCartBlock.previousElementSibling;
  const anchor = previous?.classList.contains('quantity-selector-wrapper') ? previous : addToCartBlock;

  // Already in place. Checked every pass, so a re-render never triggers a redundant move.
  if (giftBlock.nextElementSibling === anchor) return;

  row.insertBefore(giftBlock, anchor);
}

/**
 * Walks up from `element` to the ancestor that is a direct child of its block container.
 *
 * @param {Element} element
 * @param {Element | null} container - the expected parent, or null to accept either container the
 *   gift block can legitimately sit in (its original column, or the buy row once moved).
 * @returns {Element | null} the wrapper, or null if `element` is not where it was expected.
 */
function blockWrapper(element, container) {
  let node = element;

  while (node.parentElement) {
    const parent = node.parentElement;
    const found = container
      ? parent === container
      : parent.classList.contains('group-block-content') || parent.classList.contains('product-form-buttons');

    if (found) return node;
    node = parent;
  }

  return null;
}

function applyToAll() {
  for (const element of document.querySelectorAll(BUTTON_SELECTOR)) {
    applyStyles(element, BUTTON_STYLES, 'data-theme-giftnote-button-styles');
  }
  for (const element of document.querySelectorAll(CORE_SELECTOR)) {
    applyStyles(element, MODAL_STYLES, 'data-theme-giftnote-modal-styles');
  }

  if (!giftMode) return;

  for (const element of document.querySelectorAll(PRODUCT_BUTTON_SELECTOR)) {
    applyStyles(element, PRODUCT_BUTTON_GIFT_STYLES, 'data-theme-giftnote-gift-mode-styles');
  }
  promoteProductGiftButton();
}

let queued = false;

/** Coalesces bursts of mutations into a single pass per frame. */
function scheduleApply() {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    applyToAll();
  });
}

applyToAll();

/*
  Two things bring new buttons onto the page after this runs: the app hydrating an element (which
  stamps data-gn-placement onto it) and the cart drawer re-rendering through the Section Rendering
  API (which replaces the nodes outright). Watching child lists plus that one attribute covers both
  without polling.
*/
new MutationObserver(scheduleApply).observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributeFilter: ['data-gn-placement'],
});

// The app fires these on document whenever it (re)renders a button's contents.
document.addEventListener('giftnote_render_add', scheduleApply);
document.addEventListener('giftnote_render_edit', scheduleApply);

// <giftnote-core> may mount later than this module runs, so cover the modal on open too.
document.addEventListener('giftnote_open', scheduleApply);
